import { useEffect, useState } from 'react';
import { Gauge, X, RefreshCw, AlertTriangle, Clock } from 'lucide-react';
import { filesAPI, pushAPI } from '../api';
import './ExternalSubsOverview.css';

/**
 * Сводка по всем привязанным чужим подпискам в одной таблице.
 *
 * Раньше остаток трафика и срока был виден только внутри карточки конкретного файла,
 * и чтобы понять «что умрёт раньше всех», надо было обойти все файлы руками. Здесь всё
 * сразу и отсортировано по сроку: кто ближе к смерти, тот выше.
 *
 * Цифры сервер отдаёт из живого кэша, а при промахе — из снимка, сохранённого сторожем
 * лимитов. Строки со снимка помечены: они могут быть несвежими, и это надо видеть.
 */
function ExternalSubsOverview({ onClose }) {
  const [rows, setRows] = useState([]);
  const [watcher, setWatcher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const { data } = await filesAPI.externalSubsOverview();
      setRows(data.rows || []);
      setWatcher(data.watcher || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Обход всех источников идёт минуты (запросы вразбивку, чтобы не злить панель),
  // поэтому сервер отвечает сразу, а результат подтягиваем опросом.
  const checkNow = async () => {
    setChecking(true);
    try {
      await pushAPI.checkNow();
      let left = 20;
      const timer = setInterval(async () => {
        await load();
        if (--left <= 0) { clearInterval(timer); setChecking(false); }
      }, 10000);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setChecking(false);
    }
  };

  const short = (n) => String(n).replace(/\.json$/, '');
  const GB = 1024 ** 3;
  const gb = (b) => (b == null ? '—' : `${(b / GB).toFixed(1)}`);

  const dayClass = (d) => (d == null ? '' : d <= 1 ? 'crit' : d <= 7 ? 'warn' : '');
  const pctClass = (p) => (p == null ? '' : p >= 95 ? 'crit' : p >= 80 ? 'warn' : '');

  const dead = rows.filter((r) => r.lastError).length;
  const soon = rows.filter((r) => r.daysLeft != null && r.daysLeft <= 7).length;

  return (
    <div className="extov-overlay">
      <div className="extov-modal">
        <div className="extov-header">
          <h2><Gauge size={16} /> Внешние подписки</h2>
          <div className="extov-head-actions">
            <button className="btn btn-secondary btn-xs" onClick={checkNow} disabled={checking}>
              <RefreshCw size={13} className={checking ? 'extov-spin' : ''} />
              {checking ? ' Опрашиваю...' : ' Опросить все'}
            </button>
            <button onClick={onClose} className="close-btn"><X size={15} /></button>
          </div>
        </div>

        <div className="extov-info">
          {watcher?.lastRun ? (
            <span>
              Последняя проверка: {fmtAgo(watcher.lastRun)} · источников {rows.length}
              {soon > 0 && <> · <b className="extov-warn-text">скоро истекают: {soon}</b></>}
              {dead > 0 && <> · <b className="extov-crit-text">не отвечают: {dead}</b></>}
            </span>
          ) : (
            <span>Сторож ещё не делал обход — цифры из кэша, если он тёплый.</span>
          )}
          {watcher && (
            <span className="extov-thresholds">
              пороги: {watcher.pctSteps?.join('% / ')}% трафика, {watcher.daySteps?.join(' и ')} дн.
            </span>
          )}
        </div>

        {error && <div className="extov-error">{error}</div>}

        <div className="extov-content">
          {loading ? (
            <div className="extov-empty">Загрузка...</div>
          ) : rows.length === 0 ? (
            <div className="extov-empty">Ни к одному файлу не привязана внешняя подписка.</div>
          ) : (
            <table className="extov-table">
              <thead>
                <tr>
                  <th>Файл</th>
                  <th>Источник</th>
                  <th className="num">Трафик</th>
                  <th className="num">%</th>
                  <th className="num">Дней</th>
                  <th>Истекает</th>
                  <th className="num">Ссылок</th>
                  <th>Состояние</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.file + r.urlMasked} className={r.lastError ? 'row-dead' : ''}>
                    <td className="extov-file">
                      {short(r.file)}
                      {r.group && <span className="extov-group">{r.group}</span>}
                    </td>
                    <td className="extov-src" title={r.urlMasked}>
                      {r.title || r.host || '—'}
                      {!r.fresh && !r.lastError && (
                        // Цифры со снимка на диске, а не из живого кэша: после рестарта
                        // сервера это единственное, что вообще есть.
                        <span className="extov-stale" title="цифры из последнего снимка, не свежие">
                          <Clock size={11} />
                        </span>
                      )}
                    </td>
                    <td className="num">{r.total ? `${gb(r.used)} / ${gb(r.total)} ГБ` : r.used != null ? `${gb(r.used)} ГБ · безлимит` : '—'}</td>
                    <td className={`num ${pctClass(r.pct)}`}>{r.pct == null ? '—' : `${Math.round(r.pct)}%`}</td>
                    <td className={`num ${dayClass(r.daysLeft)}`}>{r.daysLeft == null ? '∞' : r.daysLeft}</td>
                    <td>{r.expire ? new Date(r.expire * 1000).toLocaleDateString('ru-RU') : 'бессрочно'}</td>
                    <td className="num">{r.links || '—'}</td>
                    <td className="extov-state">
                      {r.lastError
                        ? <span className="crit"><AlertTriangle size={12} /> {r.lastError}{r.failCount > 1 ? ` (${r.failCount})` : ''}</span>
                        : r.warned.length
                          ? <span className="warn">предупреждено: {r.warned.join(', ')}</span>
                          : <span className="ok">в норме</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="extov-footer">
          <span className="extov-hint">
            Трафик и срок — остаток <b>источника</b>, а не нашего файла. Опрос идёт по одному
            источнику вразбивку, поэтому занимает минуты.
          </span>
          <button onClick={onClose} className="btn btn-secondary">Закрыть</button>
        </div>
      </div>
    </div>
  );
}

function fmtAgo(iso) {
  if (!iso) return 'никогда';
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.round(h / 24)} дн. назад`;
}

export default ExternalSubsOverview;
